import { useEffect, useState } from "react";
import { MAX_WORD_CHARS, MAX_TABOO_CHARS, clampText } from "../../lib/tabooCardLimits";

export { MAX_WORD_CHARS, MAX_TABOO_CHARS, clampText } from "../../lib/tabooCardLimits";

export const TABOO_WORDS_PER_CARD = 3;
export const MAX_HINT_CHARS = 200;
export const MAX_ROUNDS_ALLOWED = 20;
export const MIN_POINTS = 1;
export const MAX_POINTS = 100;
export const MIN_TIMER = 1;
export const MAX_TIMER = 10;

export const TOPIC_CATEGORIES = [
  {
    id: "safe",
    label: "Safe at Work",
    subtitle: "Neutral and appropriate words\nSuitable for corporate environments",
    tone: "strictly neutral, professional, and workplace-appropriate — words must be inclusive, inoffensive, and suitable for any corporate DEI or equity training session",
  },
  {
    id: "fun",
    label: "Fun",
    subtitle: "Light, engaging, relatable content\nSuitable for mixed audiences",
    tone: "light, engaging, and relatable — upbeat and creative, suitable for a mixed professional audience without crossing professional boundaries",
  },
  {
    id: "wild",
    label: "Wild",
    subtitle: "Bold, energetic, and unexpected content\nSuitable for informal events",
    tone: "bold, energetic, and unexpected — surprising and thought-provoking words that are still appropriate for informal corporate events; push boundaries while staying respectful",
  },
];

export function newCardEntry(overrides = {}) {
  const { taboo: tabooIn, ...rest } = overrides;
  const taboo = Array.isArray(tabooIn)
    ? tabooIn.map((t) => clampText(t, MAX_TABOO_CHARS))
    : ["", "", ""];
  while (taboo.length < TABOO_WORDS_PER_CARD) taboo.push("");
  return {
    id: Date.now() + Math.random(),
    word: "",
    taboo: taboo.slice(0, TABOO_WORDS_PER_CARD),
    ...rest,
  };
}

export function isValidCardEntry(card) {
  const word = String(card?.word || "").trim();
  const taboo = (card?.taboo || []).map((t) => String(t || "").trim());
  return Boolean(
    word &&
    word.length <= MAX_WORD_CHARS &&
    taboo.length >= TABOO_WORDS_PER_CARD &&
    taboo.every((t) => t.length > 0 && t.length <= MAX_TABOO_CHARS)
  );
}

export function tabooFromRow(row) {
  const t = [
    clampText(row?.option_one, MAX_TABOO_CHARS),
    clampText(row?.option_two, MAX_TABOO_CHARS),
    clampText(row?.option_three, MAX_TABOO_CHARS),
  ];
  if (t.filter(Boolean).length >= TABOO_WORDS_PER_CARD) return t;
  const legacy = clampText(row?.option_one || row?.hint || row?.correct_answer, MAX_HINT_CHARS);
  const parts = legacy.split(/[,·|]/).map((s) => s.trim()).filter(Boolean);
  if (parts.length >= TABOO_WORDS_PER_CARD) {
    return parts.slice(0, TABOO_WORDS_PER_CARD).map((p) => clampText(p, MAX_TABOO_CHARS));
  }
  return t;
}

function parseAiResponse(responseText) {
  const clean = String(responseText || "")
    .replace(/```json/gi, "")
    .replace(/```/g, "")
    .trim();
  if (!(clean.startsWith("[") && clean.endsWith("]"))) {
    throw new Error("AI returned invalid format.");
  }
  return JSON.parse(clean);
}

function buildPrompt({ topic, categoryTone, count, excludeWords = [], literalTopic = false }) {
  const ex = Array.isArray(excludeWords)
    ? excludeWords.map((a) => String(a || "").trim()).filter(Boolean)
    : [];

  const excludeText = ex.length
    ? `
EXCLUDE LIST — do NOT use any of these as a "word" or inside "taboo" (no synonyms either):
${ex.map((a) => `- ${a}`).join("\n")}
`
    : "";

  const topicRule = literalTopic
    ? `Every card MUST relate directly to the topic "${topic}" only. Do NOT use unrelated words like Innovation, Synergy, or generic DEI terms unless they fit this topic.`
    : `Words should fit equity, workplace culture, inclusion, leadership, or the theme "${topic}".`;

  return `You are a strict JSON generator for "Equity Taboo" — a Taboo-style word game.

Return ONLY valid JSON. No markdown fences. No explanations. No extra text whatsoever.

═══════════════════════════════════
GAME SETTINGS
═══════════════════════════════════
THEME / TOPIC : "${topic}"
TONE          : ${categoryTone}
CARDS TO MAKE : ${count}
${topicRule}

═══════════════════════════════════
OUTPUT FORMAT
═══════════════════════════════════
A JSON array of exactly ${count} objects. Each object has exactly two keys:

  "word"  — EXACTLY one single word (no spaces). Title Case. Max ${MAX_WORD_CHARS} characters.
             Example for topic Animals: "Elephant", "Tiger", "Eagle"
             INVALID: multi-word phrases like "Psychological Safety"

  "taboo" — A JSON array of exactly ${TABOO_WORDS_PER_CARD} strings.
             Each string is 1 word only (no multi-word phrases), max ${MAX_TABOO_CHARS} characters each.
             These are the most obvious words a clue-giver would want to say when
             describing "word". Players must NOT use these words in clues.

═══════════════════════════════════
STRICT RULES
═══════════════════════════════════
1. "word" MUST be a single word — no spaces, no slash, no multi-word compounds, at most ${MAX_WORD_CHARS} characters.
2. Every card must have a unique "word"; never repeat the same word across cards.
3. Each taboo word must be a single word (max ${MAX_TABOO_CHARS} characters), clearly related to "word", and must not be the word itself.
4. No two taboo words within one card should mean the same thing.
5. Content must match the TONE described above exactly.
6. No slurs, no NSFW content, no content that could cause offence in a corporate setting.
7. Pick concrete, guessable single words — avoid abstract or ambiguous entries.
${excludeText}
Return ONLY the JSON array. Nothing before it. Nothing after it.`;
}

const AI_BATCH_SIZE = 9;

function normalizeAiCard(raw) {
  const word = clampText(String(raw?.word ?? raw?.question_name ?? "").trim(), MAX_WORD_CHARS);
  let taboo = [];
  if (Array.isArray(raw?.taboo)) {
    taboo = raw.taboo.map((t) => clampText(String(t || "").trim(), MAX_TABOO_CHARS)).filter(Boolean);
  } else {
    taboo = [
      raw?.taboo1 ?? raw?.taboo_1,
      raw?.taboo2 ?? raw?.taboo_2,
      raw?.taboo3 ?? raw?.taboo_3,
      raw?.option_one,
      raw?.option_two,
      raw?.option_three,
    ]
      .map((t) => clampText(String(t || "").trim(), MAX_TABOO_CHARS))
      .filter(Boolean);
  }
  const legacyHint = clampText(String(raw?.hint ?? "").trim(), MAX_HINT_CHARS);
  if (taboo.length < TABOO_WORDS_PER_CARD && legacyHint) {
    const parts = legacyHint.split(/[,·|]/).map((s) => s.trim()).filter(Boolean);
    if (parts.length >= TABOO_WORDS_PER_CARD) taboo = parts.slice(0, TABOO_WORDS_PER_CARD);
  }
  taboo = taboo.slice(0, TABOO_WORDS_PER_CARD);
  if (taboo.length < TABOO_WORDS_PER_CARD) return null;
  return { word, taboo };
}

async function fetchAiBatch({ topic, categoryTone, count, excludeWords = [], literalTopic = false, token }) {
  const safeCount = Math.min(count, AI_BATCH_SIZE);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  if (!backendBase) throw new Error("Backend URL is not configured.");
  if (!token) throw new Error("Auth token missing.");

  const resp = await fetch(`${backendBase}/generateAi`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      prompt: buildPrompt({ topic, categoryTone, count: safeCount, excludeWords, literalTopic }),
    }),
  });
  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(payload?.message || "Failed to connect to AI service.");

  const parsed = parseAiResponse(payload?.response);
  const normalized = Array.isArray(parsed)
    ? parsed.map(normalizeAiCard).filter(Boolean)
    : [];

  const excludeSet = new Set(
    excludeWords.map((a) => String(a || "").trim().toLowerCase()).filter(Boolean)
  );
  const unique = [];
  for (const q of normalized) {
    const key = String(q.word || "").trim().toLowerCase();
    if (!key || excludeSet.has(key)) continue;
    excludeSet.add(key);
    q.taboo.forEach((t) => excludeSet.add(String(t).toLowerCase()));
    unique.push(q);
  }

  return unique;
}

export async function fetchAiCards({ topic, categoryTone, count, excludeWords = [], literalTopic = false, token }) {
  const allResults = [];
  const runningExclude = [...excludeWords];
  let remaining = count;

  while (remaining > 0) {
    const batchSize = Math.min(remaining, AI_BATCH_SIZE);
    const batch = await fetchAiBatch({
      topic,
      categoryTone,
      count: batchSize,
      excludeWords: runningExclude,
      literalTopic,
      token,
    });

    if (batch.length === 0) break;

    allResults.push(...batch);
    runningExclude.push(
      ...batch.flatMap((q) => [
        String(q.word || "").trim(),
        ...(q.taboo || []).map((t) => String(t).trim()),
      ])
    );
    remaining -= batch.length;

    if (batch.length < batchSize) break;
  }

  return allResults;
}

export function aiToCardEntry(ai) {
  const normalized = normalizeAiCard(ai);
  if (!normalized) return newCardEntry();
  return newCardEntry({ word: normalized.word, taboo: normalized.taboo });
}

export function readLobbyNameFromSession() {
  try {
    const gs = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
    const ud = JSON.parse(sessionStorage.getItem("userData") || "{}");
    const l = gs?.lobby_name ?? ud?.lobby_name;
    if (l != null && String(l).trim() !== "") return String(l).trim();
  } catch {
    /* ignore */
  }
  return "";
}

export function TabooRoundCard({ card, roundIndex, isEditing, onEdit, onChange, onDelete, onKeep }) {
  const [local, setLocal] = useState(card);

  useEffect(() => {
    setLocal(card);
  }, [card]);

  const updateWord = (val) => {
    const next = { ...local, word: clampText(val, MAX_WORD_CHARS) };
    setLocal(next);
    onChange(next);
  };

  const updateTaboo = (idx, val) => {
    const taboo = [...(local.taboo || ["", "", ""])];
    taboo[idx] = clampText(val, MAX_TABOO_CHARS);
    const next = { ...local, taboo };
    setLocal(next);
    onChange(next);
  };

  const wordDisplay = String(local.word || "").trim() || `Card ${roundIndex + 1}`;

  return (
    <article className="md-taboo-card md-taboo-card--solo">
      <p className="md-taboo-card-label">Card {roundIndex + 1}</p>
      <p className="md-taboo-card-label">Your Word</p>
      {isEditing ? (
        <div className="md-taboo-card-word">
          <input
            value={local.word}
            placeholder={`Target word (max ${MAX_WORD_CHARS} chars)…`}
            maxLength={MAX_WORD_CHARS}
            onChange={(e) => updateWord(e.target.value)}
          />
        </div>
      ) : (
        <h3 className="md-taboo-card-word">{wordDisplay.toUpperCase()}</h3>
      )}
      <p className="md-taboo-card-sub">Taboo Words</p>
      <div className="md-taboo-pills">
        {(local.taboo || ["", "", ""]).slice(0, TABOO_WORDS_PER_CARD).map((t, i) => (
          <div key={i} className="md-taboo-pill">
            {isEditing ? (
              <input
                value={t}
                placeholder={`Taboo ${i + 1} (max ${MAX_TABOO_CHARS})`}
                maxLength={MAX_TABOO_CHARS}
                onChange={(e) => updateTaboo(i, e.target.value)}
              />
            ) : (
              String(t || "").trim() || "—"
            )}
          </div>
        ))}
      </div>
      <div className="md-taboo-actions">
        <button
          type="button"
          className={`md-taboo-action${!isEditing ? " md-taboo-action--active" : ""}`}
          onClick={() => onKeep(card.id)}
        >
          Keep
        </button>
        <button
          type="button"
          className={`md-taboo-action${isEditing ? " md-taboo-action--active" : ""}`}
          onClick={() => onEdit(card.id)}
        >
          Edit
        </button>
        <button type="button" className="md-taboo-action" onClick={() => onDelete(card.id)}>
          Delete
        </button>
      </div>
    </article>
  );
}
